import { useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, { type TableProps } from "@/v2-backoffice-client/components/table"
import LicenseCollectionRowToolbar from "@/v2-backoffice-client/license/collections/LicenseCollectionRowToolbar"
import {
  collectionFilterOptionsOf,
  createCollectionColumns,
  toCollectionRows,
  type CollectionRow,
} from "@/v2-backoffice-client/license/collections/license.collections.columns"
import { useInvoiceCollectionsQuery } from "@/v2-backoffice-client/license/collections/license.collections.query"
import {
  COLLECTION_STATE_CONTACTED,
  COLLECTION_STATE_ISSUE,
  COLLECTION_STATE_NOT_STARTED,
  COLLECTION_STATE_PAID,
} from "@/v2-backoffice-client/license/collections/license.collections.utils"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"

export type LicenseCollectionTableProps = Omit<
  TableProps<CollectionRow>,
  "data" | "columns"
>

// The Collections board: every invoice past due or in nonpayment with money
// outstanding, plus chased invoices that settled (the progress record) —
// the worklist that replaces the shared nonpayment spreadsheet. Facts come
// from the hourly Fenerum mirror and can never go stale; the app writes only
// the conversation. Group by CRM Owner and it is each CSM's call list;
// the pills carry the campaign's progress.
function LicenseCollectionTable(props: LicenseCollectionTableProps) {
  const { className, ...tableProps } = props

  const { data, previousData, loading, error, refetch } =
    useInvoiceCollectionsQuery()
  // previousData keeps rows rendered through post-save evictions.
  const invoices = (data ?? previousData)?.getInvoiceCollections ?? []

  const rows = useMemo(() => toCollectionRows(invoices), [invoices])

  const openRows = rows.filter((row) => row.OutcomeCode == null)
  const notStartedCount = openRows.filter(
    (row) => row.FollowUpState === COLLECTION_STATE_NOT_STARTED,
  ).length
  const contactedCount = openRows.filter(
    (row) => row.FollowUpState === COLLECTION_STATE_CONTACTED,
  ).length
  const issueCount = openRows.filter(
    (row) => row.FollowUpState === COLLECTION_STATE_ISSUE,
  ).length
  const totalOutstanding = openRows.reduce(
    (sum, row) => sum + (row.Balance ?? 0),
    0,
  )
  // Honest label: the book is NOK in practice, but a summed number must not
  // claim a currency the rows don't share.
  const currencies = new Set(
    openRows.map((row) => row.Currency).filter(Boolean),
  )
  const totalCurrency =
    currencies.size === 1 ? [...currencies][0] : currencies.size ? "mixed" : ""

  const columns = useMemo(
    () => createCollectionColumns(collectionFilterOptionsOf(rows)),
    [rows],
  )

  return (
    <Table
      className={cn("license-collection-table", className)}
      tableId="license-collection-table"
      data={rows}
      columns={columns}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        {/* Namespaced: the boards share the Licensing page, so the default
            "status" param would leak pill state between tabs. */}
        <Table.StatusFilter
          column="FollowUpState"
          urlParam="collections-status"
          isLoading={loading}
        >
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={COLLECTION_STATE_NOT_STARTED}
            variant="destructive"
            iconSrc="exclamation"
            count={notStartedCount}
          >
            Not started
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={COLLECTION_STATE_CONTACTED}
            count={contactedCount}
          >
            Contacted
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={2}
            filterValue={COLLECTION_STATE_ISSUE}
            count={issueCount}
          >
            Issue
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={3}
            filterValue={COLLECTION_STATE_PAID}
            variant="success"
            iconSrc="check-circle"
            count={rows.length - openRows.length}
          >
            Paid
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatInvoiceAmount(totalOutstanding, totalCurrency)} outstanding
        </span>

        <div className="flex-1" />
        <Table.Search column="DebtorName" placeholder="Search by customer..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => void refetch()}
          exportFileName="collections"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<CollectionRow> column="DebtorName">
        {(invoice) => (
          <LicenseCollectionRowToolbar
            invoice={invoice}
            // The payment-request draft names EVERYTHING the customer owes —
            // one email covers the debt, not one per invoice.
            customerOpenInvoices={openRows.filter(
              (row) =>
                row.CustomerId != null && row.CustomerId === invoice.CustomerId,
            )}
          />
        )}
      </Table.RowToolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicenseCollectionTable
